import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Truck, ArrowRight, X, Minus, Plus, Loader, CheckCircle, AlertTriangle } from 'lucide-react';

// --- [ NEW IMPORTS FOR REAL DATA ] ---
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { fetchStores, fetchStoresWhereStaffAssigned, fetchInventoryForStore, fetchInventoryForStoreSales } from '../apiHelper';

import FastAPIClient from '../services/apiClient';


const client = new FastAPIClient();

// --- [ Helper: Exponential Backoff ] ---
const exponentialBackoffFetch = async (fn, maxRetries = 3) => {
	for (let i = 0; i < maxRetries; i++) {
		try {
			return await fn();
		} catch (error) {
			if (i === maxRetries - 1) throw error;
			const delay = Math.pow(2, i) * 100 + Math.random() * 100;
			await new Promise(resolve => setTimeout(resolve, delay));
		}
	}
};

// --- [ Presentation Components ] ---

const StoreSelector = ({ label, stores, selectedId, onChange, disabled }) => (
	<div className="flex-1 min-w-0">
		<label className="text-xs font-semibold uppercase text-gray-500 block mb-1">{label}</label>
		<div className="relative">
			<select
				value={selectedId || ''}
				onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
				disabled={disabled}
				className={`w-full p-3 pr-10 border text-sm rounded-lg shadow-sm transition duration-150 ease-in-out appearance-none
            ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white border-gray-300 focus:ring-blue-500 focus:border-blue-500'}
          `}
			>
				<option value="" disabled>Select a Store</option>
				{stores.map(store => (
					<option key={store.id} value={store.id}>
						{store.name}
					</option>
				))}
			</select>
			<ArrowRight className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-blue-500 pointer-events-none opacity-60" />
		</div>
	</div>
);

const InventoryItemCard = ({ item, onAddToCart }) => (
	<div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition duration-200 flex flex-col justify-between h-full">
		<div>
			<h3 className="text-base font-semibold text-gray-800 truncate mb-1">{item.name}</h3>
			<p className="text-xs text-gray-500 font-mono">SKU: {item.sku}</p>
		</div>
		<div className="flex justify-between items-end mt-3">
			<div className="text-sm font-medium">
				<span className="text-gray-700">Available: </span>
				<span className="text-lg font-bold text-blue-600">{item.quantity}</span>
			</div>
			<button
				onClick={() => onAddToCart(item)}
				disabled={item.quantity <= 0}
				className="text-xs font-medium bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-full transition disabled:bg-gray-300 shadow-lg disabled:shadow-none"
				title="Add to Transfer List"
			>
				<Plus className="inline h-3 w-3 mr-1" /> Add
			</button>
		</div>
	</div>
);

const TransferCartItem = ({ item, onQuantityChange, onRemove }) => {
	const { productId, name, quantity, maxAvailable } = item;
	const isMax = quantity >= maxAvailable;
	const isMin = quantity <= 1;

	const handleUpdate = (delta) => {
		const newQuantity = Math.max(1, Math.min(maxAvailable, quantity + delta));
		onQuantityChange(productId, newQuantity);
	};

	return (
		<div className="flex items-center p-3 border-b border-gray-100 bg-white hover:bg-gray-50 transition duration-100">
			<div className="flex-1 min-w-0">
				<h4 className="text-sm font-medium text-gray-800 truncate">{name}</h4>
				<p className="text-xs text-gray-500">Max Available: {maxAvailable}</p>
			</div>

			<div className="flex items-center space-x-2 text-sm ml-4">
				<button
					onClick={() => handleUpdate(-1)}
					disabled={isMin}
					className="p-1 bg-gray-200 text-gray-600 rounded-full hover:bg-gray-300 transition disabled:opacity-50"
				>
					<Minus className="h-4 w-4" />
				</button>

				<span className={`px-3 py-1 font-bold rounded-lg ${isMax ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
					{quantity}
				</span>

				<button
					onClick={() => handleUpdate(1)}
					disabled={isMax}
					className="p-1 bg-gray-200 text-gray-600 rounded-full hover:bg-gray-300 transition disabled:opacity-50"
				>
					<Plus className="h-4 w-4" />
				</button>

				<button
					onClick={() => onRemove(productId)}
					className="p-1 text-red-500 hover:bg-red-100 rounded-full transition ml-2"
				>
					<X className="h-4 w-4" />
				</button>
			</div>
		</div>
	);
};

const TransferStatusModal = ({ status, onClose }) => {
	if (!status) return null;

	const { success, message, details, transactionId } = status;

	const Icon = success ? CheckCircle : AlertTriangle;
	const color = success ? 'text-green-500' : 'text-red-500';
	const bgColor = success ? 'bg-green-50' : 'bg-red-50';

	return (
		<div className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-50 p-4" onClick={onClose}>
			<div
				className={`bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 transform transition-all scale-100 animate-in fade-in zoom-in-95`}
				onClick={e => e.stopPropagation()}
			>
				<div className="flex flex-col items-center space-y-4">
					<Icon className={`h-16 w-16 ${color} mb-3 animate-bounce-in`} />
					<h2 className={`text-2xl font-extrabold ${color}`}>{success ? 'Transfer Successful' : 'Transfer Failed'}</h2>

					<p className="text-center text-gray-600 border-b pb-4 w-full text-sm">{message}</p>

					{transactionId && (
						<div className={`w-full p-3 rounded-lg ${bgColor} text-center font-mono text-sm border border-dashed ${color.replace('500', '300')}`}>
							<span className="font-semibold text-gray-700">Txn ID:</span> {transactionId}
						</div>
					)}

					<div className="w-full max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3 text-xs bg-gray-50">
						<h3 className="font-bold mb-1 text-gray-700">Items Processed:</h3>
						<ul className="space-y-1">
							{details && details.map((item) => (
								<li key={item.productId} className="flex justify-between text-gray-600">
									<span className="truncate">{item.name}</span>
									<span className="font-mono text-gray-800 font-bold">{item.quantity} units</span>
								</li>
							))}
							{(!details || details.length === 0) && <li className="text-gray-500 italic">No specific item details available.</li>}
						</ul>
					</div>
				</div>

				<button
					onClick={onClose}
					className="mt-6 w-full py-3 bg-blue-600 text-white font-bold rounded-xl shadow-md hover:bg-blue-700 transition"
				>
					Acknowledge & Close
				</button>
			</div>
		</div>
	);
};

// --- [ Main Container Component ] ---

const InventoryTransferManager = () => {
	const [stores, setStores] = useState([]);
	const [sourceStoreId, setSourceStoreId] = useState('');
	const [destinationStoreId, setDestinationStoreId] = useState('');

	const [inventory, setInventory] = useState([]);
	const [transferCart, setTransferCart] = useState([]);

	const [isLoadingStores, setIsLoadingStores] = useState(false);
	const [isLoadingInventory, setIsLoadingInventory] = useState(false);
	const [isTransferring, setIsTransferring] = useState(false);

	const [inventoryError, setInventoryError] = useState(null);
	const [transferStatus, setTransferStatus] = useState(null);

	const [searchTerm, setSearchTerm] = useState('');

	// --- [ Auth & Navigation ] ---
	const [loggedInUser, setLoggedInUser] = useState(null);
	const navigate = useNavigate();
	const { showToast } = useToast();

	// 1. Load User + Stores on mount
	useEffect(() => {
		const loadInitialData = async () => {
			setIsLoadingStores(true);
			const lcUser = localStorage.getItem("user");
			let parsedUser = null;

			if (lcUser) {
				try {
					parsedUser = JSON.parse(lcUser);
					setLoggedInUser(parsedUser);
				} catch (e) {
					showToast("Invalid user data. Please log in again.", "error");
					navigate('/login');
					return;
				}
			} else {
				showToast("Please log in to manage inventory transfers.", "info");
				navigate('/login');
				return;
			}

			try {
				// const fetchedStores = await exponentialBackoffFetch(() => fetchStores(parsedUser));
				const fetchedStores = await exponentialBackoffFetch(() => fetchStoresWhereStaffAssigned(parsedUser));
				const mappedStores = fetchedStores.map(store => ({
					id: Number(store.store_id),
					name: store.identifier,
				}));
				setStores(mappedStores);

				if (mappedStores.length > 0) {
					setSourceStoreId(mappedStores[0].id);
					if (mappedStores.length > 1) {
						setDestinationStoreId(mappedStores[1].id);
					}
				}
			} catch (err) {
				console.error("Failed to load stores:", err);
				showToast("Failed to load stores. Please try again.", "error");
			} finally {
				setIsLoadingStores(false);
			}
		};

		loadInitialData();
	}, [navigate, showToast]);

	// 2. Load Inventory when source store changes
	useEffect(() => {
		if (!loggedInUser || !sourceStoreId) {
			setInventory([]);
			setInventoryError(null);
			return;
		}

		const loadInventory = async () => {
			setIsLoadingInventory(true);
			setInventoryError(null);
			setInventory([]);

			const payload = {
				users_id: loggedInUser.users_id,
				logon_id: loggedInUser.logon_id,
				hpwc: loggedInUser.hpwc,
				store_id: sourceStoreId,
				offset: 0,
				limit: 10000,
			};

			try {
				// const rawInventory = await exponentialBackoffFetch(() => fetchInventoryForStore(payload));
				const rawInventory = await exponentialBackoffFetch(() => fetchInventoryForStoreSales(payload));

				// const normalizedInventory = rawInventory
				// 	.filter(item => item.catentry && item.current_quantity > 0)
				// 	.map(item => ({
				// 		productId: String(item.catentry.catentry_id),
				// 		name: item.catentry.productname.trim(),
				// 		sku: item.catentry.partnumber.trim(),
				// 		quantity: item.current_quantity,
				// 		price: item.catentry.listprice?.listprice || 0,
				// 	}));
				const inventoryMap = new Map();
				rawInventory.forEach(item => {
					if (item.catentry && item.current_quantity > 0) {
						const productId = String(item.catentry.catentry_id);
						const entry = {
							productId,
							name: item.catentry.productname.trim(),
							sku: item.catentry.partnumber.trim(),
							quantity: item.current_quantity,
							price: item.catentry.listprice?.listprice || 0,
						};
						if (inventoryMap.has(productId)) {
							const existing = inventoryMap.get(productId);
							existing.quantity += entry.quantity;
							// If details like price vary across duplicates, add logic here (e.g., take average or min)
						} else {
							inventoryMap.set(productId, entry);
						}
					}
				});
				const normalizedInventory = Array.from(inventoryMap.values());

				setInventory(normalizedInventory);

				// Sync cart with new availability
				setTransferCart(prevCart =>
					prevCart
						.map(cartItem => {
							const avail = normalizedInventory.find(i => i.productId === cartItem.productId);
							if (!avail || avail.quantity <= 0) return null;
							return {
								...cartItem,
								maxAvailable: avail.quantity,
								quantity: Math.min(cartItem.quantity, avail.quantity),
							};
						})
						.filter(Boolean)
				);
			} catch (err) {
				console.error("Failed to fetch inventory:", err);
				setInventoryError(err.message || 'Failed to load inventory.');
				showToast("Failed to load inventory.", "error");
			} finally {
				setIsLoadingInventory(false);
			}
		};

		loadInventory();
	}, [sourceStoreId, loggedInUser, showToast]);

	// --- Cart Management Logic ---

	const addToCart = useCallback((item) => {
		setTransferCart(prevCart => {
			const existingItem = prevCart.find(i => i.productId === item.productId);

			if (existingItem) {
				const newQuantity = Math.min(item.quantity, existingItem.quantity + 1);
				return prevCart.map(i =>
					i.productId === item.productId ? { ...i, quantity: newQuantity } : i
				);
			} else {
				return [
					...prevCart,
					{
						productId: item.productId,
						name: item.name,
						sku: item.sku,
						quantity: 1,
						maxAvailable: item.quantity
					}
				];
			}
		});
	}, []);

	const updateCartQuantity = useCallback((productId, quantity) => {
		setTransferCart(prevCart =>
			prevCart.map(item =>
				item.productId === productId ? { ...item, quantity } : item
			)
		);
	}, []);

	const removeFromCart = useCallback((productId) => {
		setTransferCart(prevCart =>
			prevCart.filter(item => item.productId !== productId)
		);
	}, []);

	// --- Filtering and Memoization ---

	const availableInventory = useMemo(() => {
		const lowerCaseSearch = searchTerm.toLowerCase();

		return inventory
			.filter(item => {
				const inCart = transferCart.some(cartItem => cartItem.productId === item.productId);
				const matchesSearch = item.name.toLowerCase().includes(lowerCaseSearch) || item.sku.toLowerCase().includes(lowerCaseSearch);
				return !inCart && matchesSearch;
			})
			.sort((a, b) => b.quantity - a.quantity);
	}, [inventory, transferCart, searchTerm]);

	// --- Transfer Submission (Mocked - replace later) ---
	const transferInventory = async ({ sourceStoreId, destinationStoreId, items }) => {
		console.log(`[API MOCK] Transferring from ${sourceStoreId} to ${destinationStoreId}`, items);
		await new Promise(resolve => setTimeout(resolve, 1500));

		if (sourceStoreId === destinationStoreId) {
			return { success: false, message: 'Source and destination stores cannot be the same.' };
		}
		if (items.length === 0) {
			return { success: false, message: 'Transfer list is empty.' };
		}

		return {
			success: true,
			message: `Successfully transferred ${items.length} unique items. Transaction ID: TX-${Date.now()}`,
			transactionId: `TX-${Date.now()}`,
			details: items,
		};
	};

	// -----------------------------------------------------------------
	//  REAL TRANSFER SUBMISSION (replaces the old mock + commented line)
	// -----------------------------------------------------------------
	const handleTransfer = async () => {
		if (
			transferCart.length === 0 ||
			!sourceStoreId ||
			!destinationStoreId ||
			sourceStoreId === destinationStoreId
		) {
			setTransferStatus({
				success: false,
				message: sourceStoreId === destinationStoreId
					? `Source and Destionation stores must be different locations for a valid inventory transfer. Please select distinct stores to proceed.`
					: 'Invalid transfer details. Check store selection and ensure the transfer list is not empty.',
				details: [],
			});
			return;
		}

		setIsTransferring(true);
		setTransferStatus(null);

		// ---- Build the exact payload you need for the backend ----
		const itemsToTransfer = transferCart.map(({ productId, quantity }) => ({
			productId,
			quantity,
		}));

		const transferPayload = {
			users_id: loggedInUser?.users_id,
			source_store_id: sourceStoreId,
			destination_store_id: destinationStoreId,
			items: itemsToTransfer.map(item => ({
				catentry_id: item.productId,
				quantity: item.quantity,
			})),
		};

		// console.log('[TRANSFER PAYLOAD]: ', transferPayload);

		try {
			// ---- REAL API CALL (with back-off) ----
			const apiResponse = await exponentialBackoffFetch(() =>
				client.postData('/inventory/inventory-transfer', transferPayload)
			);

			// ---- SUCCESS ----
			// Backend returns { msg: 'some success message' }
			const successMessage = apiResponse?.msg || 'Transfer completed successfully.';

			setTransferStatus({
				success: true,
				message: successMessage,
				// transactionId is optional – backend may add it later
				transactionId: apiResponse?.transactionId ?? undefined,
				details: transferCart.map(i => ({
					name: i.name,
					quantity: i.quantity,
					productId: i.productId,
				})),
			});

			// Clear cart & refresh source inventory
			setTransferCart([]);
			// Force inventory reload for the source store
			setSourceStoreId(sourceStoreId);
		} catch (err) {
			// ---- FAILURE ----
			const errorMsg =
				err?.response?.data?.detail ||
				err?.message ||
				'An unexpected error occurred during the transfer.';

			setTransferStatus({
				success: false,
				message: errorMsg,
				details: transferCart.map(i => ({
					name: i.name,
					quantity: i.quantity,
					productId: i.productId,
				})),
			});

			showToast(errorMsg, 'error');
		} finally {
			setIsTransferring(false);
		}
	};

	const handleSourceChange = (newSourceId) => {
		if (newSourceId && newSourceId === destinationStoreId === destinationStoreId) {
			alert("Source and Destination cannot be the same. Please select a different Destination Store first.");
			return;
		}
		setSourceStoreId(newSourceId || '');
	};

	const handleDestinationChange = (newDestId) => {
		if (newDestId && newDestId === sourceStoreId) {
			showToast("Source and Destination cannot be the same.", "error")
			setSourceStoreId('');
		}
		setDestinationStoreId(newDestId || '');
	};

	const totalItemsInCart = transferCart.reduce((sum, item) => sum + item.quantity, 0);

	const transferDisabled = transferCart.length === 0 || !sourceStoreId || !destinationStoreId || sourceStoreId === destinationStoreId || isTransferring || isLoadingInventory;

	return (
		<div className="min-h-screen bg-gray-50 p-4 sm:p-6 font-sans">

			{/* Header and Title */}
			<header className="mb-6 pb-4 border-b border-gray-200">
				<h1 className="text-3xl font-extrabold text-gray-800 flex items-center">
					<Truck className="w-8 h-8 mr-3 text-blue-600" />
					Inventory Transfer Manager
				</h1>
				<p className="text-sm text-gray-500 mt-1">Orchestrate stock movements between distribution centers and retail outlets.</p>
			</header>

			{/* Store Selection Panel */}
			<div className="bg-white p-5 rounded-xl shadow-lg mb-6">
				<div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
					<StoreSelector
						label="Source Location (Stock Available)"
						stores={stores}
						selectedId={sourceStoreId}
						onChange={handleSourceChange}
						disabled={isLoadingStores || isTransferring}
					/>
					<div className="flex items-center justify-center sm:w-10">
						<ArrowRight className="h-6 w-6 text-gray-400 hidden sm:block" />
						<span className="block sm:hidden text-gray-400 text-xs">TRANSFER TO</span>
					</div>
					<StoreSelector
						label="Destination Location (Stock Required)"
						stores={stores}
						selectedId={destinationStoreId}
						onChange={handleDestinationChange}
						disabled={isLoadingStores || isTransferring}
					/>
				</div>
				{sourceStoreId === destinationStoreId && sourceStoreId && (
					<p className="mt-3 text-red-500 text-sm font-medium p-2 bg-red-50 border border-red-200 rounded-lg">
						Source and Destination must be different for a transfer.
					</p>
				)}
			</div>

			{/* Main Layout: Inventory List, Cart, and Summary */}
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

				{/* Column 1: Source Inventory List */}
				<div className="lg:col-span-1 bg-white p-5 rounded-xl shadow-lg border border-gray-100 h-[70vh] flex flex-col">
					<h2 className="text-xl font-bold text-gray-700 mb-3 border-b pb-2">
						Inventory at <span className="text-blue-600">
							{stores.find(s => s.id === sourceStoreId)?.name || '...'}
						</span>
					</h2>

					<input
						type="text"
						placeholder="Search product name or SKU..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="w-full p-2 border border-gray-300 rounded-lg mb-4 text-sm focus:ring-blue-500 focus:border-blue-500"
						disabled={isLoadingInventory}
					/>

					<div className="flex-1 overflow-y-auto pr-2 -mr-2 space-y-3">
						{isLoadingInventory && (
							<div className="text-center py-10 text-gray-500">
								<Loader className="w-6 h-6 animate-spin mx-auto text-blue-500" />
								<p className="mt-2">Loading stock levels...</p>
							</div>
						)}

						{inventoryError && (
							<div className="p-4 bg-red-100 text-red-700 rounded-lg border border-red-300">
								<AlertTriangle className="h-5 w-5 inline mr-2" />
								Error: {inventoryError}
							</div>
						)}

						{!sourceStoreId && !isLoadingStores && (
							<div className="p-4 bg-yellow-100 text-yellow-700 rounded-lg border border-yellow-300 text-center">
								Select a **Source Location** above to view available inventory.
							</div>
						)}

						{!isLoadingInventory && availableInventory.length === 0 && sourceStoreId && (
							<div className="text-center py-10 text-gray-500">
								<p className="text-lg font-medium">No Matching Items</p>
								<p className="text-sm">Either all stock is already in the transfer cart, or no items match your search.</p>
							</div>
						)}

						<div className="grid grid-cols-1 gap-3">
							{availableInventory.map(item => (
								<InventoryItemCard key={item.productId} item={item} onAddToCart={addToCart} />
							))}
						</div>
					</div>
				</div>

				{/* Column 2: Transfer Cart */}
				<div className="lg:col-span-1 bg-white p-5 rounded-xl shadow-lg border border-gray-100 h-[70vh] flex flex-col">
					<h2 className="text-xl font-bold text-gray-700 mb-3 border-b pb-2 flex justify-between items-center">
						Transfer Cart
						<span className="text-sm font-mono bg-blue-100 text-blue-700 px-3 py-1 rounded-full">{transferCart.length} Unique Items</span>
					</h2>

					<div className="flex-1 overflow-y-auto pr-2 -mr-2 divide-y divide-gray-100">
						{transferCart.length === 0 ? (
							<div className="text-center py-20 text-gray-400">
								<Truck className="w-10 h-10 mx-auto mb-3" />
								<p className="text-lg font-medium">Empty Cart</p>
								<p className="text-sm">Add items from the inventory list on the left.</p>
							</div>
						) : (
							transferCart.map(item => (
								<TransferCartItem
									key={item.productId}
									item={item}
									onQuantityChange={updateCartQuantity}
									onRemove={removeFromCart}
								/>
							))
						)}
					</div>
				</div>

				{/* Column 3: Summary and Action */}
				<div className="lg:col-span-1 flex flex-col space-y-6">

					{/* Transfer Summary */}
					<div className="bg-white p-5 rounded-xl shadow-lg border border-blue-200">
						<h2 className="text-xl font-bold text-blue-700 mb-4 border-b pb-2">Transfer Details</h2>

						<div className="space-y-3 text-sm text-gray-700">
							<div className="flex justify-between items-center">
								<span className="font-semibold">Source Store:</span>
								<span className="font-medium text-gray-800">{stores.find(s => s.id === sourceStoreId)?.name || 'N/A'}</span>
							</div>
							<div className="flex justify-between items-center">
								<span className="font-semibold">Destination Store:</span>
								<span className="font-medium text-gray-800">{stores.find(s => s.id === destinationStoreId)?.name || 'N/A'}</span>
							</div>
							<div className="flex justify-between items-center pt-3 border-t border-gray-100">
								<span className="font-extrabold text-base text-gray-900">Total Items to Move:</span>
								<span className="text-2xl font-extrabold text-blue-600">{totalItemsInCart}</span>
							</div>
						</div>
					</div>

					{/* Action Button */}
					<div className="flex-1">
						<button
							onClick={handleTransfer}
							disabled={transferDisabled}
							className={`w-full flex items-center justify-center py-4 rounded-xl font-extrabold text-lg transition duration-300 shadow-xl ${transferDisabled
								? 'bg-gray-300 text-gray-600 cursor-not-allowed'
								: 'bg-green-600 text-white hover:bg-green-700 hover:shadow-2xl'
								}`}
						>
							{isTransferring ? (
								<>
									<RefreshCw className="w-5 h-5 mr-3 animate-spin" />
									Processing Transfer...
								</>
							) : (
								<>
									<Truck className="w-5 h-5 mr-3" />
									Initiate Inventory Transfer ({totalItemsInCart})
								</>
							)}
						</button>

						{transferDisabled && transferCart.length > 0 && (
							<p className="mt-3 text-red-500 text-sm font-medium p-2 bg-red-50 border border-red-200 rounded-lg text-center">
								Cannot transfer: Check store selection or wait for inventory load.
							</p>
						)}
					</div>

				</div>

			</div>

			{/* Transfer Status Modal */}
			<TransferStatusModal status={transferStatus} onClose={() => setTransferStatus(null)} />

		</div>
	);
};

export default InventoryTransferManager;